import React, { useState, useRef, useEffect, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../core/auth/AuthContext';
import {
  Menu,
  X,
  Home,
  ClipboardList,
  Settings,
  Users,
  Calendar,
  Briefcase,
  LogOut,
  Package,
  ChevronDown,
  FileText,
} from 'lucide-react';
import BossyLogo from '../ui/BossyLogo';
import md5 from 'blueimp-md5';
import ThemeToggle from '../ui/ThemeToggle';
import { Logger } from '../../core/utils/logger';
import { toError } from '../../core/utils/errorMessage';

const Navbar: React.FC = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const desktopThemeTitleId = useId();
  const mobileThemeTitleId = useId();

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  // Check if a path is active
  const isActive = (path: string) => {
    return location.pathname.includes(path);
  };

  // Toggle mobile menu
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Toggle user menu
  const toggleUserMenu = () => {
    setUserMenuOpen(!userMenuOpen);
  };

  // Generate navigation links based on user role
  const getNavLinks = () => {
    if (!user) return [];

    const baseUrl = `/${user.role}`;
    const links = [];

    // Drivers only have access to Orders page
    if (user.role === 'driver') {
      links.push({ to: `${baseUrl}/orders`, label: 'Orders', icon: Briefcase });
      return links;
    }

    // Sellers only have access to Accounts and Orders pages
    if (user.role === 'seller') {
      links.push({ to: `${baseUrl}/accounts`, label: 'Accounts', icon: Settings });
      links.push({ to: `${baseUrl}/orders`, label: 'Orders', icon: Briefcase });
      return links;
    }

    // Add dashboard link for managers only
    if (user.role === 'manager') {
      links.push({ to: `${baseUrl}/dashboard`, label: 'Dashboard', icon: Home });
    }

    // Add accounts link for all roles (workers will see only their assigned tasks)
    links.push({ to: `${baseUrl}/accounts`, label: 'Accounts', icon: Settings });

    // Add tasks link only for managers (workers use accounts page instead)
    if (user.role === 'manager') {
      links.push({ to: `${baseUrl}/tasks`, label: 'Tasks', icon: ClipboardList });
    }

    // Add orders link for managers only
    if (user.role === 'manager') {
      links.push({ to: `${baseUrl}/orders`, label: 'Orders', icon: Briefcase });
    }

    // Add ISS link for managers and workers
    if (user.role === 'manager' || user.role === 'worker') {
      links.push({ to: `${baseUrl}/iss`, label: 'ISS', icon: Package });
    }

    return links;
  };

  // Generate user menu items (Users, Rosters, Settings, Logout)
  const getUserMenuItems = () => {
    if (!user) return [];

    const baseUrl = `/${user.role}`;
    const items = [];

    // Add users link for managers
    if (user.role === 'manager') {
      items.push({ to: `${baseUrl}/users`, label: 'Users', icon: Users });
    }

    // Add rosters link only for managers
    if (user.role === 'manager') {
      items.push({ to: `${baseUrl}/roster`, label: 'Rosters', icon: Calendar });
    }

    // Add operation logs link for managers only
    if (user.role === 'manager') {
      items.push({ to: `${baseUrl}/operation-logs`, label: 'Operation Logs', icon: FileText });
    }

    // Add settings link for managers only
    if (user.role === 'manager') {
      items.push({ to: `${baseUrl}/settings`, label: 'Settings', icon: Settings });
    }

    return items;
  };

  const navLinks = getNavLinks();
  const userMenuItems = getUserMenuItems();

  return (
    <nav className="bg-black text-white shadow-md">
      <div className="container-constrained">
        <div className="flex justify-between items-center h-16">
          <div className="flex-shrink-0 font-bold text-xl flex items-center">
            <BossyLogo className="h-8 gap-1.5 mr-2" />
          </div>

          {/* Mobile menu button */}
          {user && (
            <div className="md:hidden">
              <button
                onClick={toggleMobileMenu}
                className="inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-white hover:bg-gray-700 dark:text-gray-600 dark:hover:bg-gray-300 focus:outline-none"
              >
                {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          )}

          {/* Desktop menu */}
          {user && (
            <div className="hidden md:block">
              <div className="flex items-center space-x-4">
                {navLinks.map(link => {
                  return (
                    <Link
                      key={link.to}
                      to={link.to}
                      className={`px-3 py-2 rounded-md text-base font-medium flex items-center ${
                        isActive(link.to)
                          ? 'bg-gray-800 text-white dark:bg-gray-200'
                          : 'text-gray-300 hover:bg-gray-700 dark:text-gray-700 dark:hover:bg-gray-300'
                      }`}
                    >
                      {link.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {/* User dropdown menu */}
          {user && (
            <div className="hidden md:flex items-center relative" ref={userMenuRef}>
              <button
                onClick={toggleUserMenu}
                className="flex items-center space-x-2 px-3 py-2 rounded-md hover:bg-gray-800 dark:hover:bg-gray-200 transition-colors"
              >
                <img
                  src={`https://www.gravatar.com/avatar/${user.email ? md5(user.email.trim().toLowerCase()) : ''}?d=identicon&s=28`}
                  alt="avatar"
                  className="rounded-full w-7 h-7 border border-gray-300 bg-white"
                />
                <span className="font-medium text-sm">{user.name}</span>
                <ChevronDown
                  className={`h-4 w-4 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown menu */}
              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {userMenuItems.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setUserMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <item.icon className="h-4 w-4 mr-3 text-gray-500" />
                      {item.label}
                    </Link>
                  ))}
                  <div className="border-t border-gray-200 my-1"></div>
                  <div className="flex items-center justify-between px-3 py-2">
                    <span id={desktopThemeTitleId} className="text-sm text-gray-700">
                      Theme
                    </span>
                    <ThemeToggle />
                  </div>
                  <div className="border-t border-gray-200 my-1"></div>
                  <button
                    onClick={() => {
                      setUserMenuOpen(false);
                      logout().catch(error => {
                        Logger.error('Logout function failed:', { error: toError(error) });
                      });
                    }}
                    className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <LogOut className="h-4 w-4 mr-3 text-gray-500" />
                    Logout
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Mobile menu, show/hide based on menu state */}
        {user && mobileMenuOpen && (
          <div className="md:hidden bg-gray-900 dark:bg-gray-100 pb-3 pt-2">
            <div className="px-2 space-y-1">
              {navLinks.map(link => {
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`block px-3 py-2 rounded-md text-base font-medium flex items-center ${
                      isActive(link.to)
                        ? 'bg-gray-800 text-white dark:bg-gray-200'
                        : 'text-gray-300 hover:bg-gray-700 dark:text-gray-700 dark:hover:bg-gray-300'
                    }`}
                    onClick={toggleMobileMenu}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <div className="border-t border-gray-700 dark:border-gray-300 my-3 py-2">
                <div className="px-3 text-sm text-gray-400 dark:text-gray-600">
                  Signed in as <span className="font-medium text-white">{user.name}</span>
                </div>
                {userMenuItems.map(item => (
                  <Link
                    key={item.to}
                    to={item.to}
                    className={`block px-3 py-2 rounded-md text-base font-medium flex items-center ${
                      isActive(item.to)
                        ? 'bg-gray-800 text-white dark:bg-gray-200'
                        : 'text-gray-300 hover:bg-gray-700 dark:text-gray-700 dark:hover:bg-gray-300'
                    }`}
                    onClick={toggleMobileMenu}
                  >
                    <item.icon className="h-4 w-4 mr-2" />
                    {item.label}
                  </Link>
                ))}
                <div className="flex items-center justify-between px-3 py-2">
                  <span
                    id={mobileThemeTitleId}
                    className="text-sm text-gray-300 dark:text-gray-700"
                  >
                    Theme
                  </span>
                  <ThemeToggle />
                </div>
                <button
                  onClick={() => {
                    logout().catch(error => {
                      Logger.error('Logout function failed:', { error: toError(error) });
                    });
                  }}
                  className="mt-2 w-full text-left px-3 py-2 text-base font-medium text-gray-300 hover:bg-gray-700 hover:text-white dark:text-gray-700 dark:hover:bg-gray-300 rounded-md flex items-center"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
